import { useState, useCallback, useEffect } from 'react';
// @mui
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
// routes
import { paths } from 'src/routes/paths';
// types
import { HTBaiViet } from 'src/types/htblog';
// components
import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// sections
import { useHome } from 'src/sections/home/hooks';
import BlogSort from '../blog-sort';
import BlogList from '../blog-list';
import BlogSearch from '../blog-search';
// hooks (local)

// ----------------------------------------------------------------------

const BLOG_SORT_OPTIONS = [
  { value: 'latest', label: 'Mới nhất' },
  { value: 'oldest', label: 'Cũ nhất' },
  { value: 'popular', label: 'Phổ biến' },
];

export default function BlogListHomeView() {
  const settings = useSettingsContext();
 
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');

  const { homeBlogs, homeBlogsStatus, getHomeBlogs } = useHome();

  const handleSortBy = useCallback((newValue: string) => {
    setSortBy(newValue);
  }, []);

  const handleSearch = useCallback(async (inputValue: string) => {
    setSearchQuery(inputValue);
    // filtering is done on the fly when computing sortedBlogs below
  }, []);

  useEffect(() => {
    getHomeBlogs();
    setSortBy('latest');
  }, [getHomeBlogs]);


  // Sort blogs based on sortBy value
  const baseBlogs = searchQuery
    ? homeBlogs.filter(
        (b) =>
          b.tieuDe.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (b.moTaNgan || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : homeBlogs;

  const sortedBlogs = [...baseBlogs].sort((a, b) => {
    switch (sortBy) {
      case 'latest':
        return new Date(b.ngayTao).getTime() - new Date(a.ngayTao).getTime();
      case 'oldest':
        return new Date(a.ngayTao).getTime() - new Date(b.ngayTao).getTime();
      case 'popular':
        return (b.luotXem || 0) - (a.luotXem || 0);
      default:
        return 0;
    }
  });

  const notFound = !homeBlogsStatus.loading && !sortedBlogs.length;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Bài viết" 
        links={[
          {
            name: 'Trang chủ',
            href: '/',
          },
          {
            name: 'Bài viết',
          },
        ]}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <Stack
        spacing={3}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-end', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      >
        <BlogSearch
          search={{ query: searchQuery, results: sortedBlogs }}
          onSearch={handleSearch}
          hrefItem={(slug: string) => paths.blog.details(slug)}
        />

        <BlogSort
          sort={sortBy}
          onSort={handleSortBy}
          sortOptions={BLOG_SORT_OPTIONS}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && notFound ? (
        <EmptyContent
          filled
          title="Không tìm thấy bài viết"
          action={
            <Button
              component="a"
              href={paths.dashboard.root}
              startIcon={<Iconify icon="eva:arrow-ios-back-fill" width={16} />}
              sx={{ mt: 3 }}
            >
              Quay lại Dashboard
            </Button>
          }
          sx={{
            py: 10,
          }}
        />
      ) : null}

      {!loading && !notFound ? (
        <BlogList posts={sortedBlogs} loading={loading} />
      ) : null}
    </Container>
  );
}